/**
 * The wizard's order (startContentCreation params) as the v2 order fields —
 * the shape computePath() reads. The wizard is one of the ways of input
 * (§12.1.2): it only fills the order.
 */
import { isComicStyle } from '@/contexts/illustrations/services/styleTechnique';
import type { WizardScenario } from '@/contexts/content/types/enums';
import type { OrderFields } from './types';

export interface WizardOrderInput {
  scenario: WizardScenario;
  sourceEntityId?: string;
  params?: Record<string, unknown>;
}

const WITH_AUDIO: readonly WizardScenario[] = ['SC-02', 'SC-04', 'SC-05', 'SC-09', 'SC-11'];
const WITH_BOOK: readonly WizardScenario[] = ['SC-03', 'SC-04', 'SC-06', 'SC-10', 'SC-11'];
/** Layers added to a story that already exists: its text is not written again. */
export const EXTEND_SCENARIOS: readonly WizardScenario[] = ['SC-05', 'SC-06', 'SC-11'];
const FROM_TEXT: readonly WizardScenario[] = ['SC-08', 'SC-09', 'SC-10'];

const hasReplaceMap = (p: Record<string, unknown>) => Array.isArray(p.replaceMap) && p.replaceMap.length > 0;

function fromTextOp(p: Record<string, unknown>): string {
  switch (p.fromTextMode) {
    case 'verbatim': return 'keep';
    case 'translate': return 'translate';
    case 'recast_hero': return 'replace';
    case 'incorporate': return 'add';
    case 'based_on': {
      const base = p.fromTextBasedOnMode === 'reimagine' ? 'reimagine' : 'retell';
      // the parent picked whom their heroes replace (the wizard's parse): «… + замена»
      return hasReplaceMap(p) ? `${base}_replace` : base;
    }
    default: return 'keep';
  }
}

/** Photo heroes are records with an id from the upload (photoHeroIds ⊂ characterIds). */
function heroesOf(p: Record<string, unknown>): string {
  const ids = Array.isArray(p.characterIds) ? (p.characterIds as unknown[]).map(String) : [];
  const photoIds = new Set(Array.isArray(p.photoHeroIds) ? (p.photoHeroIds as unknown[]).map(String) : []);
  // v1 drafts (a saved wizard session from before upload-time creation)
  const drafts = Array.isArray(p.pendingPhotoHeroes) ? p.pendingPhotoHeroes.length : 0;
  const photos = [...photoIds].filter((id) => ids.includes(id)).length + drafts;
  const lib = ids.filter((id) => !photoIds.has(id)).length > 0;
  if (photos && lib) return 'mix';
  if (photos > 1) return 'photos';
  if (photos === 1) return 'photo1';
  return lib ? 'lib' : 'none';
}

function illOf(style: string | undefined): string {
  if (style === 'coloring') return 'color';
  return isComicStyle(style) ? 'comic' : 'book';
}

function startOf(p: Record<string, unknown>): string {
  if (p.autoGenerateCharacterPortraits === false) return 'none';
  if (p.autoGeneratePageImages === false) return 'chars';
  return 'all';
}

/** Exports the parent ordered in the output step (PDF / print-ready / video). */
/**
 * The PDF the order asks for, when it says more than «a PDF»: paper ('a4' | 'trim'), the
 * artwork to the edge of the sheet, an upscale (paid), a print-shop ZIP. These become the
 * pdf layer's params, which the export reads (exports/server.ts). Unknown keys are dropped.
 */
export function pdfExportOptions(p: Record<string, unknown>): Record<string, unknown> {
  const x = ((p.exports ?? {}) as Record<string, unknown>).pdf;
  if (!x || typeof x !== 'object') return {};
  const o = x as Record<string, unknown>;
  return {
    ...(o.paper === 'a4' || o.paper === 'trim' ? { paper: o.paper } : {}),
    ...(typeof o.edgeFill === 'boolean' ? { edgeFill: o.edgeFill } : {}),
    ...(typeof o.hd === 'boolean' ? { hd: o.hd } : {}),
    ...(typeof o.archive === 'boolean' ? { archive: o.archive } : {}),
    ...(typeof o.imagesOnly === 'boolean' ? { imagesOnly: o.imagesOnly } : {}),
  };
}

function exportsOf(p: Record<string, unknown>): { pdf: boolean; print: boolean; video: boolean } {
  const x = (p.exports ?? {}) as Record<string, unknown>;
  // pdf: true, or the file's options ({ paper, edgeFill, hd, archive } — pdfExportOptions)
  const pdf = x.pdf === true || (!!x.pdf && typeof x.pdf === 'object');
  return { pdf, print: x.print === true, video: x.video === true };
}

function voiceOf(p: Record<string, unknown>): string {
  const vs = (p.voiceSettings ?? {}) as Record<string, unknown>;
  return vs.source === 'cloned' || vs.voiceSource === 'cloned' || vs.isClone === true ? 'clone' : 'preset';
}

export function orderFromWizard(input: WizardOrderInput): { fields: OrderFields; storyExists: boolean } {
  const p = input.params ?? {};
  const s = input.scenario;
  const storyExists = EXTEND_SCENARIOS.includes(s);
  const input_ = storyExists || s === 'SC-07' ? 'story' : FROM_TEXT.includes(s) ? (p.basisStoryId ? 'classic' : 'paste') : 'form';
  const op = s === 'SC-07' ? 'translate' : storyExists ? 'none' : FROM_TEXT.includes(s) ? fromTextOp(p) : 'new';
  const style = typeof p.illustrationStyle === 'string' ? p.illustrationStyle : undefined;
  const book = WITH_BOOK.includes(s);
  const audio = WITH_AUDIO.includes(s);
  const x = exportsOf(p);
  // layers added to a story whose book is already drawn: its exports come from that book
  const existingBook = storyExists && !book && typeof p.existingIllustrationId === 'string' && p.existingIllustrationId ? p.existingIllustrationId : null;
  const pages = book || !!existingBook;
  const fields: OrderFields = {
    input: input_,
    v2: true,
    op,
    heroes: heroesOf(p),
    req: false,
    ill: book ? illOf(style) : 'none',
    start: book ? startOf(p) : 'all',
    narr: audio ? voiceOf(p) : 'none',
    // a PDF and the print file are made from the pages; the video from pages + audio
    x_pdf: pages && x.pdf,
    x_print: pages && x.print,
    x_video: pages && audio && x.video,
    // the YouTube audio publication stays in admin → publishing
    x_pub: false,
    fail: 'none',
    edit: 'none',
    scenario: s,
    ...(typeof p.language === 'string' ? { language: p.language } : {}),
    ...(style ? { style } : {}),
    ...(typeof p.illustrationFormat === 'string' ? { format: p.illustrationFormat } : {}),
    ...(existingBook ? { illExisting: existingBook } : {}),
  };
  return { fields, storyExists };
}

/**
 * «Спросить родителя» (node 'ask'): heroes were picked but the operation does
 * not use them — a kept text (verbatim) or a retelling. The parent either
 * switches to «add heroes» or removes them. translate never asks: its hero
 * selection is hidden (the model drops it).
 */
export function heroesAskOf(input: WizardOrderInput): 'keep' | 'retell' | null {
  const p = input.params ?? {};
  if (p.heroesAddConfirmed === true) return null;
  const { fields } = orderFromWizard(input);
  if (fields.heroes === 'none') return null;
  if (fields.op === 'keep') return 'keep';
  if (fields.op === 'retell') return 'retell';
  return null;
}
